/**
 * Hooks para el Centro de Reportes — 7 reportes exportables.
 */
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

export interface ReportParams {
  date_from?: string
  date_to?: string
}

export interface ReportResponse<T> {
  data: T[]
  summary: Record<string, string | number>
  generated_at: string
  currency: string
}

// ─── Tipos por reporte ────────────────────────────────────────────────────────

export interface SalesReportRow {
  status: string
  document_type: string
  count: number
  subtotal: string
  tax_amount: string
  total: string
}

export interface ARAgingRow {
  document_id: string
  customer_name: string
  document_type: string
  issue_date: string | null
  due_date: string | null
  days_overdue: number
  total: string
  outstanding: string
  bucket: string
}

export interface PurchasesReportRow {
  /** Proveedor del grupo: con él se pide el detalle (#1488). Opcional porque un
   *  backend anterior no lo devuelve; sin él la fila no se expande. */
  supplier_id?: string
  supplier_name: string
  status: string
  /** Etiqueta legible del estado (choices del modelo, igual que el listado). */
  status_display?: string
  count: number
  subtotal: string
  tax_amount: string
  total: string
}

export interface APAgingRow {
  invoice_id: string
  supplier_name: string
  issue_date: string | null
  due_date: string | null
  days_overdue: number
  status: string
  total: string
  bucket: string
}

export interface FactoringReportRow {
  operation_id: string
  entity_name: string
  operation_date: string | null
  status: string
  total_amount_ceded: string
  notes: string
}

export interface SalesByProductRow {
  product_id: string
  name: string
  quantity: string
  revenue: string
  doc_count: number
}

export interface CustomerAnalysisRow {
  customer_id: string
  customer_name: string
  tax_id: string
  total_revenue: string
  doc_count: number
  last_document_date: string | null
}

export interface CostCenterDistributionRow {
  cost_center_id: string
  code: string
  name: string
  center_type: 'cost' | 'revenue' | 'profit'
  center_type_label: string
  revenue: string
  expense: string
  margin: string
  /** Centro tipo «costo» que recibió ingresos: señal de que está mal tipificado. */
  has_unexpected_revenue: boolean
}

/** Margen de contribución escalonado: la estructura no se prorratea, se resta al final. */
export interface ContributionSummary {
  contribution_margin: string
  structure_assigned: string
  structure_unassigned: string
  structure_total: string
  operating_result: string
  passthrough_revenue: string
  passthrough_expense: string
  passthrough_margin: string
  /** Centros de costo con ingresos: su ingreso NO entra al margen hasta retipificarlos. */
  mistyped_centers: { code: string; name: string; revenue: string }[]
}

export interface CostCenterDistributionResponse
  extends ReportResponse<CostCenterDistributionRow> {
  contribution: ContributionSummary
}

export interface EightColumnBalanceRow {
  account_code: string
  account_name: string
  account_type: string
  debitos: string
  creditos: string
  deudor: string
  acreedor: string
  activo: string
  pasivo: string
  perdida: string
  ganancia: string
}

export interface EightColumnBalanceResponse {
  date_from: string
  date_to: string
  count: number
  accounts: EightColumnBalanceRow[]
  totals: Record<string, string>
  resultado_ejercicio: string
  generated_at: string
  currency: string
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useSalesReport(params: ReportParams = {}) {
  return useQuery<ReportResponse<SalesReportRow>>({
    queryKey: ['report', 'sales', params],
    queryFn: () => apiClient.get('/reports/sales/', { params }).then(r => r.data),
    staleTime: 5 * 60_000,
  })
}

export function useARAgingReport(params: ReportParams = {}) {
  return useQuery<ReportResponse<ARAgingRow>>({
    queryKey: ['report', 'ar-aging', params],
    queryFn: () => apiClient.get('/reports/ar-aging/', { params }).then(r => r.data),
    staleTime: 5 * 60_000,
  })
}

export function usePurchasesReport(params: ReportParams = {}) {
  return useQuery<ReportResponse<PurchasesReportRow>>({
    queryKey: ['report', 'purchases', params],
    queryFn: () => apiClient.get('/reports/purchases/', { params }).then(r => r.data),
    staleTime: 5 * 60_000,
  })
}

export function useAPAgingReport(params: ReportParams = {}) {
  return useQuery<ReportResponse<APAgingRow>>({
    queryKey: ['report', 'ap-aging', params],
    queryFn: () => apiClient.get('/reports/ap-aging/', { params }).then(r => r.data),
    staleTime: 5 * 60_000,
  })
}

export function useFactoringReport(params: ReportParams = {}) {
  return useQuery<ReportResponse<FactoringReportRow>>({
    queryKey: ['report', 'factoring', params],
    queryFn: () => apiClient.get('/reports/factoring/', { params }).then(r => r.data),
    staleTime: 5 * 60_000,
  })
}

export function useSalesByProductReport(params: ReportParams = {}) {
  return useQuery<ReportResponse<SalesByProductRow>>({
    queryKey: ['report', 'sales-by-product', params],
    queryFn: () => apiClient.get('/reports/sales-by-product/', { params }).then(r => r.data),
    staleTime: 5 * 60_000,
  })
}

export function useCustomerAnalysisReport(params: ReportParams = {}) {
  return useQuery<ReportResponse<CustomerAnalysisRow>>({
    queryKey: ['report', 'customer-analysis', params],
    queryFn: () => apiClient.get('/reports/customer-analysis/', { params }).then(r => r.data),
    staleTime: 5 * 60_000,
  })
}

export function useCostCenterDistributionReport(params: ReportParams = {}) {
  return useQuery<CostCenterDistributionResponse>({
    queryKey: ['report', 'cost-center-distribution', params],
    queryFn: () => apiClient.get('/reports/cost-center-distribution/', { params }).then(r => r.data),
    staleTime: 5 * 60_000,
  })
}

export function useEightColumnBalance(params: ReportParams = {}) {
  return useQuery<EightColumnBalanceResponse>({
    queryKey: ['report', 'eight-column-balance', params],
    queryFn: () => apiClient.get('/accounting/reports/eight-column-balance/', { params }).then(r => r.data),
    staleTime: 5 * 60_000,
  })
}

// ─── Comisiones ───────────────────────────────────────────────────────────────

export interface CommissionReportRow {
  order_number: string
  order_date: string
  seller_name: string
  sku: string
  commission_base: string
  commission_base_label: string
  commission_rate: string
  margin_rate: string
  line_net: string
  margin_amount: string
  commission_amount: string
  /** Nivel de la cascada que resolvió la tasa: trato del vendedor para el
   *  producto, su comisión por defecto, la del producto, o ninguno. */
  commission_source: string
  commission_source_label: string
}

export function useCommissionReport(params: ReportParams & { seller_id?: string } = {}) {
  return useQuery<ReportResponse<CommissionReportRow>>({
    queryKey: ['report', 'commissions', params],
    queryFn: () => apiClient.get('/reports/commissions/', { params }).then(r => r.data),
    staleTime: 5 * 60_000,
  })
}

// ─── F29 ─────────────────────────────────────────────────────────────────────

export interface F29Data {
  period: { month: number; year: number }
  ventas: { neto: string; iva_debito: string; by_doc_type: { code: string; name: string; count: number; cant_code: string | null; iva_code: string | null; neto_code?: string | null; neto: string; iva: string }[] }
  compras: {
    count: number; neto: string; iva_credito: string
    by_purchase_type?: { key: string; name: string; count: number; cant_code: string | null; neto_code: string | null; iva_code: string | null; neto: string; iva: string }[]
  }
  retenciones: string
  honorarios?: { count: number; total: string; retencion_2da_cat: string }
  iva_neto: string
  ppm?: { base: string; tasa: string; modo: string; regimen: string; monto: string }
  impuesto_unico?: string
  total_a_pagar?: string
  codigos?: {
    total_debitos: string; iva_credito: string; total_creditos: string
    base_ppm?: string; ppm: string; retencion_2da_cat: string
    impuesto_unico?: string; iva_determinado: string; total_a_pagar: string
  }
  resultado: 'a_pagar' | 'a_favor'
}

export function useF29(params: { month: number; year: number }) {
  return useQuery<F29Data>({
    queryKey: ['reports', 'f29', params],
    queryFn: async () => { const r = await apiClient.get('/reports/f29/', { params }); return r.data },
    enabled: !!params.month && !!params.year,
    staleTime: 5 * 60_000,
  })
}

export interface F22BreakdownRow {
  code: string; name: string; saldo: string
}
export interface F22Credito {
  code: string; label: string; monto: string
}
export interface F22Data {
  year: number
  tasa: string
  tasa_origen?: 'manual' | 'regimen' | 'default'
  regimen?: string
  tasa_label: string
  ingresos: string
  gastos: string
  utilidad: string
  es_perdida: boolean
  impuesto_estimado: string
  ppm_pagado: string
  ppm_account_code: string | null
  creditos?: F22Credito[]
  total_creditos?: string
  impuesto_neto: string
  resultado: 'a_pagar' | 'a_devolver' | 'cero'
  anio_tributario?: number
  reajuste_art72_factor?: string
  reajuste_art72_pct?: string
  reajuste_art72_monto?: string
  impuesto_neto_reajustado?: string
  codigos?: { base_imponible: string; ppm: string; reajuste_art72?: string }
  ingresos_breakdown: F22BreakdownRow[]
  gastos_breakdown: F22BreakdownRow[]
  disclaimer: string
}

export function useF22(params: { year: number; tasa?: string }) {
  return useQuery<F22Data>({
    queryKey: ['reports', 'f22', params],
    queryFn: async () => {
      const r = await apiClient.get('/reports/f22/', { params })
      return r.data
    },
    enabled: !!params.year,
    staleTime: 5 * 60_000,
  })
}

export interface F29SaleRow {
  id: string; document_type: string; number: string; issue_date: string | null
  customer_name: string; customer_tax_id: string
  subtotal: string; tax_amount: string; total: string
}
export interface F29PurchaseRow {
  id: string; invoice_number: string; document_type_sii: string; issue_date: string | null
  supplier_name: string; supplier_tax_id: string
  subtotal: string; tax_amount: string; total: string; status: string
}
export interface F29HonorariosRow {
  id: string; bhe_number: string; issue_date: string | null
  issuer_name: string; issuer_rut: string
  total_honorarios: string; impuesto_honorarios: string; liquido_honorarios: string
}
export type F29DetailsType = 'sales' | 'purchases' | 'honorarios'
export interface F29DetailsResponse<T> {
  period: { month: number; year: number }
  type: F29DetailsType
  results: T[]
  count: number
  page: number
  page_size: number
}

export function useF29Details(params: {
  type: F29DetailsType
  month: number
  year: number
  doc_type?: string
  page?: number
  page_size?: number
  enabled?: boolean
}) {
  const { enabled = true, ...query } = params
  return useQuery<F29DetailsResponse<F29SaleRow | F29PurchaseRow | F29HonorariosRow>>({
    queryKey: ['reports', 'f29', 'details', query],
    queryFn: async () => {
      const r = await apiClient.get('/reports/f29/details/', { params: query })
      return r.data
    },
    enabled: enabled && !!params.month && !!params.year,
    staleTime: 5 * 60_000,
  })
}
